<template>
  <div
    class="Dropdown"
    v-click-outside="debounce(handleClick, 200)"
    v-show="notDefault"
  >
    <button
      role="button"
      type="button"
      ref="toggle"
      class="Dropdown__Toggle"
      @click="toggleMenu"
    >
      <span class="hidden">Dropdown Toggle</span>
      <template v-if="isColor">
        <span class="Dropdown__Value">
          <component
            :is="conditionalElement(selected)"
            v-bind="conditionalProps(selected)"
          ></component>
        </span>
      </template>
      <template v-else>
        <span class="Dropdown__Value">{{ selected }}</span>
      </template>
      <Icon icon="chevron-down" />
    </button>
    <Teleport to=".upsell-dropdown-placeholder">
      <div ref="menu" :style="floatingStyles" :class="menuClasses">
        <ul class="Dropdown__Menu-List">
          <li v-for="value in values" :key="value" class="Dropdown__Menu-Item">
            <button
              role="button"
              type="button"
              @click="handleSelection(value)"
              class="Dropdown__Menu-Btn"
            >
              <template v-if="isColor">
                <span class="Dropdown__Value">
                  <component
                    :is="conditionalElement(value)"
                    v-bind="conditionalProps(value)"
                  ></component>
                  <span>{{ truncateText(value) }}</span>
                </span>
              </template>
              <template v-else>
                <span
                  class="Dropdown__Value"
                  data-ht-element="option"
                  :data-ht-id="selected"
                >
                  {{ truncateText(value) }}
                </span>
              </template>
              <Icon :class="{ active: isSelected(value) }" icon="checkmark" />
            </button>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import Icon from './Icon.vue';
import { ref, computed } from 'vue';
import { pubSub } from '@utils/PubSub';
import { useFloating, flip, autoUpdate, offset } from '@floating-ui/vue';
import { debounce, removeSoldOutFromText } from '@utils/global-functions';

const props = defineProps({
  /**
   * @property {String} selected is the selected value for this dropdown input.
   * @example 'Ferrari'
   */
  selected: { type: String, required: true },
  /**
   * @property {Object} option is an object containing a key for the option name and a key for its values.
   * @example
   * option = { name: "Cars", values: ['Mercedes', 'Ferrari', 'Aston Martin'] }
   */
  option: { type: Object, required: true, default: false },
  /**
   * @property {Number} characterLimit is a optional prop. This prop will be a number that is the desired character limit for option values.
   */
  characterLimit: { type: Number, required: false },
  location: { type: String, required: false },
});

const emits = defineEmits(['handle-select']);

const isColor = computed(() => props.option.name === 'Color');
const isSelected = computed(() => {
  return (value) => {
    return value === props.selected;
  };
});

const notDefault = computed(
  () =>
    props.option.name !== 'Title' ||
    !props.option.values.includes('Default Title')
);

const toggle = ref(null);
const menu = ref(null);
const open = ref(false);
const navOffset = ref(0);

const setOffset = () => {
  const header = document.querySelector('.section-header');
  const nano = parseInt(header.style.top.split('px')[0]);
  navOffset.value = header.scrollHeight + nano;
};

const determinePlacement = () => {
  const location = props.location ?? '';
  const isProduct = location === 'pdp';
  const placement = isProduct ? 'top' : 'top-start';
  return placement;
};

setOffset();
window.addEventListener('scroll', setOffset);

const { floatingStyles } = useFloating(toggle, menu, {
  placement: determinePlacement(),
  strategy: 'fixed',
  middleware: [
    flip({
      padding: navOffset.value,
    }),
    offset({
      mainAxis: 8,
      crossAxis: 0,
    }),
  ],
  whileElementsMounted: autoUpdate,
});

const menuClasses = computed(() => {
  const isOpen = open.value;
  const className = 'Dropdown__Menu';
  return isOpen ? `${className} ${className}--Active` : className;
});

const values = computed(() => {
  const values = props.option?.values ?? [];
  return Array.from(new Set(values));
});

const retrieveSwatch = (value) => {
  const global = window.Brooklinen || {};
  const swatches = global?.product?.swatches ?? [];
  const match = swatches.find((swatch) => swatch.name === value) ?? false;
  return match;
};

const conditionalElement = computed(() => {
  return (value) => {
    const text = removeSoldOutFromText(value);
    const swatch = retrieveSwatch(text);
    if (!swatch) return 'span';

    const isHex = swatch.value.charAt(0) === '#';
    return isHex ? 'div' : 'img';
  };
});

const conditionalProps = computed(() => {
  return (value) => {
    const text = removeSoldOutFromText(value);
    const swatch = retrieveSwatch(text);
    if (!swatch) return {};

    const isHex = swatch.value.charAt(0) === '#';
    const defaultProperties = {
      class: 'Dropdown__Swatch',
      'data-ht-element': 'swatch',
      'data-ht-id': props.selected,
      alt: props.selected,
    };
    const properties = {};
    if (isHex) {
      properties.role = 'presentation';
      properties.style = {
        background: swatch.value,
        backgroundPosition: 'center',
      };
    } else {
      properties.src = swatch.value;
    }
    return { ...properties, ...defaultProperties };
  };
});

const truncateText = (value) => {
  const text = value;
  const containsUnavailableMessaging = value.includes('Sold out');
  const hasCharacterLimit = props?.characterLimit ?? false;
  if (!hasCharacterLimit) return text;

  if (containsUnavailableMessaging) {
    const parsed = value.split('(');
    if (parsed[0].length > props.characterLimit) {
      const truncated = `${parsed[0].substring(0, props.characterLimit)}...`;
      return `${truncated} (${parsed[1].trim()}`;
    } else {
      return text;
    }
  } else {
    if (text.length > props.characterLimit) {
      const truncated = `${text.substring(0, props.characterLimit)}...`;
      return truncated;
    } else {
      return text;
    }
  }
};

const toggleMenu = () => (open.value = !open.value);
const handleSelection = (value) => {
  toggleMenu();
  emits('handle-select', value);
};

pubSub.subscribe('upsell:move', () => {
  if (open.value) toggleMenu();
  return;
});

const handleClick = () => {
  const notOpen = open.value !== true;
  if (notOpen) return;
  toggleMenu();
};
</script>
